const TOKEN_PREFIX = 'spcc:google-calendar:';
const FLOW_PREFIX = 'spcc:google-oauth:';
const SCOPE = 'https://www.googleapis.com/auth/calendar.readonly';
const validToken = token => typeof token?.access_token === 'string' && !!token.access_token &&
  Number.isFinite(token.expires_at) && token.expires_at > Date.now() + 30000;
const activeFlows = new Set();
let sessionGeneration = 0;

function remove(storage, key) { try { window[storage].removeItem(key); } catch { /* Storage may be disabled. */ } }

export function clearGoogleCalendarSession() {
  sessionGeneration++;
  for (const cancel of activeFlows) cancel();
  try { Object.keys(sessionStorage).filter(key => key.startsWith(TOKEN_PREFIX)).forEach(key => sessionStorage.removeItem(key)); } catch { /* Session storage may be disabled. */ }
  remove('localStorage', 'google-calendar-token');
}

export class GoogleCalendarService {
  constructor(userId) {
    this.userId = userId;
    this.accessToken = null;
    this.expiresAt = 0;
    this.isAuthenticated = false;
    this.needsReconnect = false;
    this.generation = sessionGeneration;
    this.cancelAuth = null;
  }
  get tokenKey() { return `${TOKEN_PREFIX}${this.userId}`; }

  async initialize() {
    // Never adopt the old unscoped token, including when returning from a popup.
    remove('localStorage', 'google-calendar-token');
    if (!this.userId || this.generation !== sessionGeneration) { this.reset(); return; }
    if (validToken({ access_token: this.accessToken, expires_at: this.expiresAt })) return;
    this.reset();
    try {
      const saved = JSON.parse(sessionStorage.getItem(this.tokenKey));
      if (saved?.user_id === this.userId && validToken(saved)) this.acceptToken(saved);
      else remove('sessionStorage', this.tokenKey);
    } catch { remove('sessionStorage', this.tokenKey); }
  }

  reset() { this.accessToken = null; this.expiresAt = 0; this.isAuthenticated = false; }
  acceptToken(token) {
    if (!validToken(token)) throw new Error('Google returned an expired or invalid token. Reconnect Google Calendar.');
    this.accessToken = token.access_token;
    this.expiresAt = token.expires_at;
    this.isAuthenticated = true;
    this.needsReconnect = false;
    try { sessionStorage.setItem(this.tokenKey, JSON.stringify({ ...token, user_id: this.userId })); } catch { /* The current tab can still use its in-memory token. */ }
  }

  async authenticate() {
    if (!this.userId) throw new Error('Sign in to SPCC before connecting Google Calendar.');
    if (this.generation !== sessionGeneration) throw new Error('Your SPCC session changed. Reload before reconnecting Google Calendar.');
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) throw new Error('Google Calendar is not configured. Set the Google client ID first.');
    this.cancelAuth?.();
    this.reset();
    remove('sessionStorage', this.tokenKey);
    remove('localStorage', 'google-calendar-token');
    const state = crypto.randomUUID(), pendingKey = `${FLOW_PREFIX}${state}`, resultKey = `${pendingKey}:result`;
    const expires = Date.now() + 5 * 60000;
    try { localStorage.setItem(pendingKey, JSON.stringify({ user_id: this.userId, expires_at: expires })); }
    catch { throw new Error('Allow browser storage to connect Google Calendar.'); }
    const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');
    for (const [key, value] of Object.entries({ client_id: clientId, redirect_uri: `${window.location.origin}/google-callback.html`,
      response_type: 'token', scope: SCOPE, state, prompt: 'consent select_account', include_granted_scopes: 'true' })) authUrl.searchParams.set(key, value);

    return new Promise((resolve, reject) => {
      let popup, timer, poll, finished = false;
      const finish = (error, token) => {
        if (finished) return;
        finished = true;
        clearTimeout(timer); clearInterval(poll);
        window.removeEventListener('message', onMessage);
        window.removeEventListener('storage', onStorage);
        activeFlows.delete(cancel);
        this.cancelAuth = null;
        remove('localStorage', pendingKey); remove('localStorage', resultKey);
        if (error) { reject(error); return; }
        try { this.acceptToken(token); resolve(true); } catch (e) { reject(e); }
      };
      const cancel = () => finish(new Error('Google Calendar sign-in was cancelled. Connect again to continue.'));
      const receive = result => {
        if (finished || result?.state !== state || result.provider !== 'google') return;
        if (this.generation !== sessionGeneration) { cancel(); return; }
        if (result.type === 'calendar-auth-error') { finish(new Error(result.error || 'Google Calendar access was denied.')); return; }
        if (result.type !== 'calendar-auth-success') return;
        const scopes = result.tokens?.scope?.split(' ');
        if (scopes && !scopes.includes(SCOPE) && !scopes.includes('https://www.googleapis.com/auth/calendar')) {
          finish(new Error('Calendar access was not granted. Reconnect Google and allow calendar access.')); return;
        }
        finish(null, result.tokens);
      };
      const onMessage = event => { if (event.origin === window.location.origin && event.source === popup) receive(event.data); };
      const readResult = () => {
        // COOP can sever window.opener. Read only this nonce's callback result;
        // never inspect popup.closed or fall back to an old calendar token.
        try { const value = localStorage.getItem(resultKey); if (value) receive(JSON.parse(value)); } catch { /* Retry until callback or timeout. */ }
      };
      const onStorage = event => { if (event.key === resultKey) readResult(); };
      window.addEventListener('message', onMessage);
      window.addEventListener('storage', onStorage);
      activeFlows.add(cancel); this.cancelAuth = cancel;
      timer = setTimeout(() => finish(new Error('Google sign-in did not finish. Close the sign-in window and connect again.')), 5 * 60000);
      poll = setInterval(readResult, 500);
      try { popup = window.open(authUrl.href, `google-calendar-${state}`, 'width=500,height=600'); }
      catch { finish(new Error('Allow popups for SPCC, then connect Google Calendar again.')); return; }
      if (!popup) finish(new Error('Allow popups for SPCC, then connect Google Calendar again.'));
    });
  }

  async makeApiCall(endpoint) {
    await this.initialize();
    if (!this.isAuthenticated) throw new Error('Reconnect Google Calendar to load your events.');
    const token = this.accessToken;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 30000);
    let response;
    try {
      response = await fetch(`https://www.googleapis.com/calendar/v3${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` }, signal: controller.signal,
      });
    } finally { clearTimeout(timer); }
    if (this.generation !== sessionGeneration || this.accessToken !== token) throw new Error('Your Google Calendar connection changed. Reload the events.');
    if (response.status === 401) {
      this.disconnect(); this.needsReconnect = true;
      throw new Error('Google Calendar access expired or was revoked. Select Connect Google to sign in again.');
    }
    if (response.status === 403) throw new Error('Google Calendar access was denied. Reconnect and allow calendar access; your administrator may need to enable the Calendar API.');
    if (!response.ok) throw new Error(`Google Calendar could not load events (${response.status}). Try again.`);
    return response.json();
  }

  async getEvents(startDate, endDate) {
    if (!Number.isFinite(startDate?.getTime()) || !Number.isFinite(endDate?.getTime()) || endDate <= startDate) throw new Error('Choose a valid start and end date.');
    const base = `/calendars/primary/events?timeMin=${encodeURIComponent(startDate.toISOString())}&timeMax=${encodeURIComponent(endDate.toISOString())}&singleEvents=true&orderBy=startTime&maxResults=250`;
    const items = [], seen = new Set(); let pageToken = '';
    do {
      const data = await this.makeApiCall(base + (pageToken ? `&pageToken=${encodeURIComponent(pageToken)}` : ''));
      items.push(...(data.items || []));
      pageToken = data.nextPageToken || '';
      if (pageToken && seen.has(pageToken)) throw new Error('Google Calendar repeated a page. Reload your calendar.');
      seen.add(pageToken);
    } while (pageToken);
    return items;
  }

  async getCalendars() { return (await this.makeApiCall('/users/me/calendarList')).items || []; }
  disconnect() {
    this.cancelAuth?.(); this.reset();
    remove('sessionStorage', this.tokenKey); remove('localStorage', 'google-calendar-token');
  }
}
